import { Button, IndexTable, Text, Thumbnail } from '@shopify/polaris';
import { useParams } from 'react-router-dom';
import { discountCapLabel, discountDatesLabel } from '../../../../utils/discountLimits';
import { Campaign, DiscountType } from '../../../../utils/interfaces/campaign';
import { IRuleRowData } from '../../../../utils/interfaces/shopify';
import { displayWithTwoDecimals } from '../../../../utils/numUtils';
import { DeleteRuleButton } from './DeleteRuleButton';

interface IProps {
  rule: IRuleRowData;
  index: number;
  campaign: Campaign;
}

export const ShopifyCampaignRuleRow = ({ rule, index, campaign }: IProps): JSX.Element => {
  const { appName } = useParams<{ appName: string }>();
  let sanitizedDiscountValue = rule.discountValue?.toString();
  if (rule.restrictionLabel === 'none') {
    sanitizedDiscountValue = 'N/A';
  } else if (rule.discountType === DiscountType.Percent) {
    sanitizedDiscountValue += ' %';
  } else {
    // amount
    sanitizedDiscountValue = displayWithTwoDecimals(sanitizedDiscountValue) + ' $';
  }

  return (
    <IndexTable.Row id={rule.id} position={index}>
      <IndexTable.Cell>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          <Thumbnail
            size="small"
            source={rule.product.productData.image?.src ?? ''}
            alt={`Image for product ${rule.product.productData.title}`}
          />
          <span style={{ whiteSpace: 'normal', maxWidth: '10rem' }}>
            {rule.product.productData.title}
          </span>
        </div>
      </IndexTable.Cell>
      <IndexTable.Cell>{rule.network}</IndexTable.Cell>
      <IndexTable.Cell>
        {/* Shortened so the Discount column (now carrying cap + dates) stays
            on-screen; full address on hover. */}
        <span title={rule.contract}>
          {rule.contract && rule.contract.length > 14
            ? `${rule.contract.slice(0, 6)}…${rule.contract.slice(-4)}`
            : rule.contract}
        </span>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <span style={{ whiteSpace: 'normal' }}>{rule.restrictionLabel}</span>
      </IndexTable.Cell>
      <IndexTable.Cell>{rule.restrictionCriteria}</IndexTable.Cell>
      <IndexTable.Cell>
        {/* Cap and dates sit under the discount rather than in their own
            columns: the table already scrolls sideways on the full contract
            address, and new columns would land off-screen. */}
        <div
          style={{
            display: 'flex',
            flexFlow: 'column',
            gap: '0.25rem',
            whiteSpace: 'normal',
            minWidth: '9rem',
            maxWidth: '11rem',
          }}
        >
          <Text as="span" fontWeight="semibold">
            {sanitizedDiscountValue}
          </Text>
          {rule.restrictionLabel !== 'none' && (
            <>
              <Text as="span" tone="subdued">
                Cap: {discountCapLabel(rule.maxRedemptions)}
              </Text>
              <Text as="span" tone="subdued">
                Dates: {discountDatesLabel(rule)}
              </Text>
            </>
          )}
        </div>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <div
          style={{ display: 'flex', flexFlow: 'column', alignItems: 'flex-start', gap: '0.5rem' }}
        >
          <Button
            variant="plain"
            url={`/shopify/${appName}/campaigns/${campaign.id}/rules?edit=${encodeURIComponent(
              rule.id
            )}&product=${rule.product.productId}`}
          >
            Edit
          </Button>
          <DeleteRuleButton
            campaign={campaign}
            productId={rule.product.productId}
            ruleId={rule.id}
          />
        </div>
      </IndexTable.Cell>
    </IndexTable.Row>
  );
};
